import React, { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import TextField from '@mui/material/TextField'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Button from '@mui/material/Button'
import EditIcon from '@mui/icons-material/Edit'
import { useTheme } from '@mui/material/styles'
import moment from 'moment'
import { useSmartTranslation } from '../../../../generic/hooks/useSmartTranslation'
import { useAuthorization } from '../../../../generic/hooks/useAuthorization'
import { useDateFormatter } from '../../../../generic/hooks/useDateFormatter'
import useFrontEndConfig from '../../../../generic/hooks/useFrontEndConfig/useFrontEndConfig'
import useFabricationOrderClusterSendSetting from '../../../../generic/hooks/useFabricationOrderClusterSendSetting'
import useGetExtendedColumns from '../../../../generic/hooks/useGetExtendedColumns/useGetExtendedColumns'
import { fetchPlanTypes, fetchClients } from '../detailApi'
import { changedFieldLabels } from '../changedFieldLabels'
import CollapsedFieldsLine from './CollapsedFieldsLine'
import FabricationOrderWorkflowField from '../FabricationOrderWorkflowField'
import { CONTROL_RADIUS } from '../detailControls'
import { statusProgressLabel } from '../../statusProgressLabel'
import { toInstantIso } from '../detailDates'
import { systemValueLabel } from '../../../../generic/systemValues/systemValueLabel'
import { EMPTY_DASH } from '../detailShared'
import useGeneralSave from './useGeneralSave'
import GeneralSaveBar from './GeneralSaveBar'
import {
  BlockCard, BooleanFieldRow, DateTimeFieldRow, DetailDateTimePicker, EditableFieldRow, ExtendedListInput, FieldRow,
  ParentFoFieldRow, ReadOnlyField, ReferenceFieldRow, SelectFieldRow, SheetPendingFieldRow, SheetPendingValue, fieldSx, formatExtendedValue
} from './generalFieldRows'

// Enum text -> i18n key lives in `orders/statusProgressLabel.js`, shared with the table's column (story 178,
// S73/S74). R-20/C1: this is rendered verbatim as text, never as a bar or a percentage, and never converted
// to a number — the numeric progress bar of the header reads `progressPercent` instead (see progressLabel.js).

// R-7/#30 precedent (isPlannedEndDateLate, FabricationOrder.jsx): late only when both dates are
// present, parseable, and plannedEndDate is strictly after targetDate. Fail-safe: never marks late
// on missing/malformed input.
const isPlannedEndDateLate = (plannedEndDate, targetDate) => {
  if (!plannedEndDate || !targetDate) return false
  const planned = moment(plannedEndDate, moment.ISO_8601, true)
  const target = moment(targetDate, moment.ISO_8601, true)
  if (!planned.isValid() || !target.isValid()) return false
  return planned.isAfter(target)
}

const GeneralBlock = ({
  header,
  sheet,
  extendedColumnsOverride,
  onOpenModifyQuantity = () => {},
  fabricationOrderId,
  unsavedChanges,
  onSaved = () => {},
  // ⚠ Verificado en la app (pasada 24): X2 llega DESPUÉS de que el bloque ya se pueda editar. Si el
  // usuario teclea y guarda en esa ventana, `sheetSource` es `{}` y el cuerpo sale con cuatro
  // campos en vez de la fila entera — exactamente el guardado destructivo que la pasada 11 vino a
  // impedir (capturado: PUT con `{targetDate, deliveryDate, priority, expectedLastModified}` y 500
  // de Sun). Mientras la fila no esté, no se guarda.
  sheetReady = true,
  // Task 29 fix r1: X2's first read failed (not "still loading"); `onRetrySheet` reads it again.
  sheetError = false,
  onRetrySheet = () => {},
  registerSave = () => {},
  // H179: tras cambiar el workflow (C2), el contenedor avisa y recarga la ficha.
  onWorkflowAssigned = () => {},
  // El `AlertUI` de la ficha, para los avisos del workflow (el de General se desmonta al recargar).
  showAlert = () => {}
}) => {
  const { t_ } = useSmartTranslation()
  const theme = useTheme()
  const { format, formatDate, formatDateTime } = useDateFormatter()
  const isUserAuthorized = useAuthorization()
  const [frontendConfig] = useFrontEndConfig()
  const inCluster = frontendConfig?.inCluster || false
  const clusterSendSettingEnabled = useFabricationOrderClusterSendSetting(inCluster)
  const clusterFabricationOrderSyncEnabled = inCluster && clusterSendSettingEnabled
  const fetchedExtendedColumns = useGetExtendedColumns('fabrication_order')
  const extendedColumns = extendedColumnsOverride !== undefined ? extendedColumnsOverride : (fetchedExtendedColumns || [])

  const source = header || {}
  // X2 (`fabricationOrder?fabricationOrder=`, `04-casos-uso.md` CU-L2 step 1) — the FULL order row,
  // fetched alongside N1. General reads its non-N1 fields from here (`createdNode`, the FLAT
  // `extended_column1..12` fields, `workflow.used`) AND resends this SAME row whole on save
  // (buildSaveBody below): N1 (`header`) keeps serving identity/situation/quantities/deviation/pause/
  // progressPercent as before, it is never the save source (plan-jupiter.md §2, defect fix pasada 11).
  const sheetSource = sheet || {}
  const canEdit = isUserAuthorized('EDIT_FABRICATION_ORDERS')
  // ADDENDUM 19 A (S342) + ADDENDUM 21 A (E27, S356): ¿hay PEDIDO? La frontera convierte el
  // `order: null` de N1 en `{}`, así que la pregunta es si el objeto trae algo — y se mira el
  // PEDIDO, no la línea: la línea cuelga del pedido, no al revés. Comprobado contra N1 en vivo: sin
  // pedido llegan los dos a null a la vez, nunca uno sí y otro no.
  const hasOrder = Object.keys(source.order || {}).length > 0
  // ⚠️ El CLIENTE es independiente del pedido EN LOS DOS SENTIDOS, y por eso se pregunta aparte.
  // En N1 es un campo de primer nivel, hermano de `order`, leído de la columna propia de la orden:
  // 27 órdenes de 110 tienen cliente y no tienen pedido, y otras 4 tienen pedido y no tienen
  // cliente. Sacar el cliente DEL pedido dejaría las 27 vacías y le inventaría uno a esas 4 — los
  // dos fallos a la vez y ninguno visible sin buscarlo.
  const hasClient = Object.keys(source.client || {}).length > 0
  // ⚠️⚠️ REGRESIÓN DE PARIDAD (auditoría del PO): el cliente ERA EDITABLE en la ventana antigua
  // —columna «Client code», un `smartSelect` sobre `clientsBasicData` (`FabricationOrder.jsx:520`)—
  // y al encender el interruptor esa columna pasó a solo lectura sin que el detalle recogiera la
  // edición. Resultado: la única forma de cambiarle el cliente a una orden desapareció de la
  // aplicación entera. No es una función nueva de esta historia: es una que se cayó entre las dos
  // ventanas, y por eso se devuelve tal cual estaba, no "mejorada".
  //
  // 📌 El campo se ofrece aunque la orden NO tenga cliente: si solo saliera con cliente presente,
  // una orden sin él seguiría sin poder recibir uno — que es justo la mitad de la regresión.
  //
  // ⚠️ Y NO se ofrece si el cliente llega SIN id. Sun vincula por id y lee un objeto sin id como
  // "desvincular" (200, silencioso): sin id no hay forma de preseleccionarlo en la lista, y
  // cualquier interacción con ella lo borraría. Hoy N1 lo sirve como `IdCodeNameDto`, así que el
  // caso no debería darse; se declara en vez de suponerse.
  const clientIsAddressable = !hasClient || !!source.client?.id
  const clientEditable = canEdit && clientIsAddressable

  // RN28/RN76/RN30: pending/snapshot live in the container (useUnsavedChanges) so they survive
  // this component unmounting while another block is active (S95) — GeneralBlock only reads/writes
  // through the shared object, it never owns the batch itself. A local fallback keeps this
  // component usable in isolation (existing T1b tests mount it without a container).
  const [localUnsavedChanges] = useState(() => ({
    pending: {},
    snapshot: {},
    hasChanges: false,
    freshMarker: { value: '', forLastModified: '' },
    setFreshMarker: () => {},
    savedBatch: { header: {}, headerValues: {}, sheet: {}, sheetValues: {} },
    setSavedBatch: () => {},
    setExtendedColumns: () => {},
    setField: () => {},
    discard: () => {}
  }))
  const batch = unsavedChanges || localUnsavedChanges

  // Catálogo real (`smartpm_cell.fabrication_order_plan_type` vía `getFabricationOrderPlanTypes`,
  // el MISMO endpoint que ya usaba la columna del listado): si mañana se siembra una tercera fila,
  // aquí aparece sola. Una lectura por apertura del bloque, no por render.
  const [planTypes, setPlanTypes] = useState([])
  useEffect(() => {
    let alive = true
    // `Promise.resolve().then(...)` y no `fetchPlanTypes()` a pelo: el catálogo es accesorio, y si la
    // lectura falla —o revienta de forma síncrona— el bloque tiene que seguir mostrando el valor
    // actual, nunca caerse entero por un desplegable.
    Promise.resolve()
      .then(fetchPlanTypes)
      .then((types) => { if (alive) setPlanTypes(types || []) })
      .catch(() => { if (alive) setPlanTypes([]) })
    return () => { alive = false }
  }, [])
  // Catálogo de clientes (`clientsBasicData`, el MISMO que servía el desplegable de la ventana
  // antigua). Mismo trato que el de tipos de plan: una lectura por apertura del bloque y, si falla,
  // la ficha sigue mostrando el cliente actual en vez de caerse por un desplegable.
  const [clients, setClients] = useState([])
  useEffect(() => {
    let alive = true
    Promise.resolve()
      .then(fetchClients)
      .then((list) => { if (alive) setClients(list || []) })
      .catch(() => { if (alive) setClients([]) })
    return () => { alive = false }
  }, [])

  // Task 25: after a successful save the dialog re-reads N1 and X2 in the background, and until
  // each answer lands this block still receives the OLD row. What was just saved stays on screen
  // meanwhile: the saved batch is kept together with the rows it was saved against, and each side
  // only applies while that SAME object is still the current one. As soon as a new `header` or
  // `sheet` arrives (the dialog only swaps them when a read lands), the real row wins — derived
  // during render, so not even one frame shows the overlay over the new row, and even if the row
  // says something else (another user, or Sun normalising the value).
  // Task 29: each side keeps its own copy, so a second save can MERGE into the side whose row has
  // not been re-read yet and start afresh on the side whose row already has.
  // Kept in the container too: coming back to General while the re-read failed must still show it.
  const { savedBatch } = batch
  const savedOverHeader = savedBatch.header === header ? savedBatch.headerValues : {}
  const savedOverSheet = savedBatch.sheet === sheet ? savedBatch.sheetValues : {}
  const has = (object, field) => Object.prototype.hasOwnProperty.call(object, field)
  const savedOr = (overlay, field, fallback) => (has(overlay, field) ? overlay[field] : fallback)

  const valueOf = (field) => (batch.pending[field] !== undefined ? batch.pending[field] : savedOr(savedOverHeader, field, source[field]))
  const isModified = (field) => has(batch.pending, field)
  // El valor que maneja el desplegable de cliente es el ID EN TEXTO: el DOM de un `<select>` solo
  // habla en texto, y mezclar número y texto haría que la preselección fallara justo cuando el
  // usuario no ha tocado nada. Vacío = sin cliente, que es un valor legítimo (`client: null`
  // desvincula), no una ausencia de dato.
  const clientPersisted = savedOr(savedOverHeader, 'client', source.client?.id ? String(source.client.id) : '')
  const clientValue = isModified('client') ? batch.pending.client : clientPersisted

  // `{ id, code }` (N1) -> the selector's `{ id, fabricationOrder }`. Without an id the parent cannot
  // be addressed, so it is shown but not offered for editing (same rule as the client).
  const parentFoPersisted = savedOr(savedOverHeader, 'parentFo', source.parentFabricationOrder?.id ? { id: source.parentFabricationOrder.id, fabricationOrder: source.parentFabricationOrder.code } : '')
  const parentFoValue = isModified('parentFo') ? batch.pending.parentFo : parentFoPersisted
  const planTypePersisted = savedOr(savedOverSheet, 'planType', sheetSource.fabricationOrderPlanTypeDto?.code || source.planType?.code || '')

  // The planned end is judged against the target date just saved while N1 has not been re-read.
  const plannedEndDateLate = isPlannedEndDateLate(source.plannedEndDate, toInstantIso(savedOr(savedOverHeader, 'targetDate', source.targetDate)))

  // The loaded value of each N1 field, as the control shows it: writing it back is not a change.
  const originalOf = (field) => {
    if (field === 'client') return clientPersisted
    if (field === 'parentFo') return parentFoPersisted
    if (field === 'planType') return planTypePersisted
    return savedOr(savedOverHeader, field, source[field])
  }

  const handleFieldChange = (field, value) => {
    batch.setField(field, value, originalOf(field))
    clearFieldError(field)
  }

  // ⚠ Fix (escalada del hub, 2026-09-10, evidencia verificada: Sun's `FabricationOrderDto.java:77-99`
  // declares 12 FLAT fields `extended_column1..12`, `grep -c 'extendedColumnValues'` on that file is
  // 0 — the wrapper object never existed on the wire. Jupiter's own row already carries them flat
  // (`orders/__tests__/RequestFormEmptyPayload.test.jsx:246-260`, `order.extended_column2` etc). An
  // extended field is therefore just another flat field of the X2 row (`column.field` IS the real
  // column name, e.g. `extended_column1` — same as `useGetExtendedColumns` already gives the
  // listing), read/written exactly like `targetDate`/`priority` above, only sourced from `sheetSource`
  // (X2) instead of `source` (N1, which never carried it either).
  const handleSheetFieldChange = (field, value) => {
    batch.setField(field, value, savedOr(savedOverSheet, field, sheetSource[field]))
    clearFieldError(field)
    // Forecast and its expiration date are read together by the same rule, so touching either
    // answers an error on the date.
    if (field === 'forecast') clearFieldError('expirationDate')
  }

  const extendedValueOf = (field) =>
    (batch.pending[field] !== undefined ? batch.pending[field] : savedOr(savedOverSheet, field, sheetSource[field]))

  const {
    fieldErrors, clearFieldError, saveError, conflict, deliveryConfirmation, saving,
    handleSave, handleConfirmDelivery, handleCancelDeliveryConfirmation, handleDiscard
  } = useGeneralSave({
    batch, header, sheet, fabricationOrderId, extendedColumns, valueOf, extendedValueOf, isModified, sheetSource, onSaved, registerSave
  })

  // The prompt (container) names the touched fields; it cannot read the installation's extended
  // columns itself, so the block hands them over.
  const { setExtendedColumns } = batch
  const extendedColumnsKey = extendedColumns.map((column) => `${column.field}:${column.name}`).join('|')
  useEffect(() => {
    if (extendedColumns.length > 0) setExtendedColumns(extendedColumns)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extendedColumnsKey])

  // ═══ §2.3 — LO QUE ESTÁ VACÍO BAJA A UNA LÍNEA ═══
  //
  // Lienzo, tablero «General»: el pie recoge lo que no tiene nada que enseñar en vez de gastar
  // tarjetas enteras en decirlo. En una orden sin pedido, sin previsión y sin columnas extendidas
  // —el caso corriente— eran TRES tarjetas para anunciar tres ausencias.
  //
  // ⚠️⚠️ PLEGAR NO ES OCULTAR: la línea ENUMERA cada cosa plegada CON SU VALOR («origen comercial
  // —, nodo creador —, campos adicionales ninguno definido»), así que todo se sigue leyendo sin
  // abrir nada, y «Ver todos los campos» devuelve las tarjetas enteras.
  //
  // ⚠️⚠️ Y SOLO SE PLIEGA LO VACÍO. Un grupo con datos no se pliega jamás — si al plegar
  // desapareciera un dato, sería ocultarlo.
  // ⚠️⚠️ LA REGLA, UNA SOLA Y PARA LOS TRES: SE PLIEGA LO QUE NO TIENE NADA QUE ENSEÑAR **NI NADA
  // QUE PONER**. Un grupo vacío y de solo lectura no aporta más que su ausencia, y ésa cabe en una
  // línea; pero un grupo vacío con un control dentro no está diciendo «no hay dato», está diciendo
  // «escríbelo aquí» — y eso ya lo tiene escrito este fichero unas líneas más arriba, en el
  // comentario de `FieldRow`: «un blanco en un campo EDITABLE significa "escribe aquí" y no "no
  // hay dato"».
  //
  // 📌 No es una sutileza: el cliente se acaba de recuperar como campo editable porque cambiarlo
  // había dejado de ser posible en toda la aplicación. Plegar su hueco por estar vacío lo metería
  // un clic más abajo justo después, deshaciendo a medias lo que se arregló. Lo mismo vale para la
  // previsión: con permiso, «desactivada» es un valor que se puede cambiar, no un hueco.
  //
  // ⚠️ El lienzo dibuja la fecha de expiración plegada CON la previsión a la vista, y aquí se
  // pliegan las dos juntas o ninguna. Se elige la regla uniforme: partir una tarjeta por la mitad
  // exigiría decidir campo a campo qué se pliega, y esa decisión es justo la que diverge.
  const commercialOriginFoldable = !hasOrder && !hasClient && !clientEditable
  // Task 29: not before X2 lands — an absent row is not an empty forecast.
  const forecastFoldable = sheetReady && !canEdit && !extendedValueOf('forecast') && !extendedValueOf('expirationDate')
  // Los campos adicionales son la excepción que confirma la regla: cuando la instalación no ha
  // definido ninguno no hay nada que enseñar NI nada que poner —no existe el campo—, así que se
  // pliega tenga permiso quien mire o no.
  const additionalFieldsFoldable = extendedColumns.length === 0
  const foldedSummary = [
    commercialOriginFoldable ? `${t_('Sales order')} ${EMPTY_DASH}` : '',
    forecastFoldable ? `${t_('Forecast expiration date')} ${EMPTY_DASH}` : '',
    additionalFieldsFoldable ? `${t_('Additional fields')}: ${t_('No additional fields defined')}` : ''
  ].filter((entry) => !!entry)

  const touchedCount = Object.keys(batch.pending).length
  // Lienzo, tablero «Cambios sin guardar»: la barra NOMBRA los campos. «2 cambios sin guardar»
  // obliga a repasar la pantalla entera para encontrarlos; con los nombres delante no obliga a
  // nada. Es el mismo criterio que ya rige el aviso de conflicto, que enumera los campos que tocó
  // el otro usuario en vez de decir cuántos.
  const labelOfChange = (change) => (change.labelKey ? t_(change.labelKey) : change.label)
  const touchedLabels = changedFieldLabels(batch.pending, extendedColumns).map(labelOfChange)
  // Sun's `changedFields` are field names; the user reads the same labels as in the bar.
  const conflictFieldLabel = (field) => labelOfChange(changedFieldLabels({ [field]: true }, extendedColumns)[0])

  // ⚠️ La nota «dato de sistema» vuelve a esta tarjeta, que es donde estaba antes de las secciones.
  // Subió a la cabecera de «identidad» mientras esa cabecera existió; al retirarla el PO, se repone
  // donde estaba en vez de borrarse — lo que quitó son los dos rótulos que añadí yo, no una nota
  // que ya llevaba meses puesta.
  const identificationCard = (
    <BlockCard
      testId='group-identification-and-process'
      title={t_('Identification and process')}
      note={t_('System data — not editable')}
    >
      <FieldRow testId='code-field' label={t_('Code')} value={source.code} editable={false} />
      <FieldRow testId='part-reference-field' label={t_('Part reference')} value={source.partReference?.code} editable={false} />
      {/* ⚠️⚠️ EL NODO CREADOR SE MUDA AQUÍ, y no es un capricho: la tarjeta donde vivía pasa a
          llamarse «Pedido», y este campo no habla del pedido — dice DÓNDE SE CREÓ LA ORDEN. Bajo
          ese rótulo estaría mal etiquetado, y un campo bajo un título que no le corresponde es
          peor que uno mal colocado: el título afirma algo falso sobre él.
          Los campos que el encargo da para la tarjeta renombrada son TRES —pedido, línea y fecha
          del pedido—, así que sacarlo de ahí es lo que esa lista pide; a dónde va lo decide lo que
          el campo dice, y lo que dice es identidad de la orden.
          📌 En la pasada 26 se movió a procedencia porque la tarjeta de Previsión se quedaba vacía
          y mal nombrada. Aquel motivo no se contradice: lo que ha cambiado es que su tarjeta de
          acogida ahora se llama por el pedido. */}
      {clusterFabricationOrderSyncEnabled && (
        sheetReady
          ? <FieldRow testId='creator-node-field' label={t_('Creator node')} value={sheetSource.createdNode?.name} editable={false} absence={t_('Not reported')} />
          : <SheetPendingFieldRow testId='creator-node-field' label={t_('Creator node')} failed={sheetError} />
      )}
    </BlockCard>
  )

  const quantitiesCard = (
    <BlockCard testId='group-quantities' title={t_('Quantities')}>
      {/* ⚠️⚠️ ESTO REVOCA LA PRIMERA MITAD DE S285, que decía «General muestra la cantidad
        total COMO HOY». Como hoy era `totalQuantity`, y `totalQuantity` no es la cantidad de
        la orden: es `countActiveParts`, un recuento de FILAS DE PIEZA que cuenta también las
        CANCELADAS —`active` es una marca de borrado lógico y una pieza cancelada sigue con
        `active = true`—. Con 20 pedidas y 10 canceladas decía 30.
        Un campo rotulado «Cantidad total» que cuenta filas de una tabla no responde a la
        pregunta que su rótulo hace. Decisión del PO: el de la ORDEN. La segunda mitad de S285
        —que la cabecera muestre la pedida— no se toca: se refuerza. */}
      <FieldRow testId='total-quantity-field' label={t_('Total quantity')} value={source.quantity?.nominal} editable={false} />
      <Box data-testid='good-quantity-field' data-editable='false'>
        {/* The quantity is not edited in place: the pencil at the end of the box opens Modify
            quantity (pasada 19), where the lock sits on the other read-only fields. */}
        <ReadOnlyField
          label={t_('Manufactured quantity')}
          endAdornment={canEdit && (
            <IconButton
              data-testid='modify-quantity-pencil'
              aria-label={t_('Modify quantity')}
              title={t_('Modify quantity')}
              size='small'
              edge='end'
              onClick={onOpenModifyQuantity}
            >
              <EditIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        >
          {`${source.quantity?.good === null || source.quantity?.good === undefined || source.quantity?.good === '' ? '—' : source.quantity.good} / ${source.quantity?.nominal === null || source.quantity?.nominal === undefined || source.quantity?.nominal === '' ? '—' : source.quantity.nominal}`}
        </ReadOnlyField>
      </Box>
      {/* ⚠ Fix (pasada 21, defecto 4): the raw enum (`PLANNED`) used to print verbatim — same
        vocabulary the header's status chip already translates it with (DetailHeader.jsx,
        `t_(source.status || '')`), reused here rather than a second map. */}
      <FieldRow testId='status-field' label={t_('Status')} value={systemValueLabel(t_, 'FabricationOrderGlobalStatusEnum', source.status || '')} editable={false} />
      <Box data-testid='status-progress-field' data-editable='false'>
        {/* ⚠️ RN8 — sin progreso de estado, el guion. Un renglón vacío bajo su rótulo no dice
            «esta orden no tiene progreso declarado»: se lee como un dato que no ha llegado. */}
        <ReadOnlyField label={t_('Status progress')}>{source.statusProgress ? statusProgressLabel(t_, source.statusProgress) : EMPTY_DASH}</ReadOnlyField>
      </Box>
    </BlockCard>
  )

  const planningEditableCard = (
    <BlockCard testId='group-planning-and-delivery-editable' title={t_('Planning and delivery')}>
      <DateTimeFieldRow
        testId='target-date-field' label={t_('Target date')} field='targetDate'
        value={valueOf('targetDate')} editable={canEdit} modified={isModified('targetDate')}
        onChange={handleFieldChange} error={fieldErrors.targetDate}
        dateTimeFormat={format.dateTime} formatDateTime={formatDateTime}
        clearable={false}
      />
      <DateTimeFieldRow
        testId='delivery-date-field' label={t_('Delivery date')} field='deliveryDate'
        value={valueOf('deliveryDate')} editable={canEdit} modified={isModified('deliveryDate')}
        onChange={handleFieldChange} error={fieldErrors.deliveryDate}
        dateTimeFormat={format.dateTime} formatDateTime={formatDateTime}
      />
      {/* ⚠️⚠️ S413 — ADDENDUM 53 E / 54 C: la FECHA MÍNIMA DE INICIO, EDITABLE. El PO decidió «se
          añaden los dos» (con el cliente, `5a075bd24`): con el modal encendido el listado deja de
          editarla, y sin esto no se podía cambiar en ningún sitio. Un campo editable necesita las
          DOS puertas, y las tiene: N1 la sirve (antes no, y ofrecerla vacía habría escrito encima
          del valor real) y N16 la acepta. Mismo selector y mismo contrato que las demás fechas
          (S450). No es obligatoria: Clear la borra. Sin permiso se lee, y su ausencia se DICE (RN8):
          en `LTT001-HUN001` llega `null`. */}
      <DateTimeFieldRow
        testId='min-start-date-field' label={t_('Min start date')} field='minStartDate'
        value={valueOf('minStartDate')} editable={canEdit} modified={isModified('minStartDate')}
        onChange={handleFieldChange} error={fieldErrors.minStartDate}
        dateTimeFormat={format.dateTime} formatDateTime={formatDateTime}
        absence={t_('Not reported')}
      />
      <EditableFieldRow
        testId='priority-field' label={t_('Priority')} field='priority'
        value={valueOf('priority')} editable={canEdit} modified={isModified('priority')}
        onChange={handleFieldChange} error={fieldErrors.priority} inputMode='numeric'
      />
      {sheetReady
        ? (
          <SelectFieldRow
            testId='plan-type-field' label={t_('Fabrication order plan type')} field='planType'
            value={isModified('planType') ? batch.pending.planType : planTypePersisted}
            options={planTypes} editable={canEdit} modified={isModified('planType')}
            onChange={handleFieldChange} error={fieldErrors.planType}
          />
          )
        : <SheetPendingFieldRow testId='plan-type-field' label={t_('Fabrication order plan type')} failed={sheetError} />}
      <ParentFoFieldRow
        testId='parent-fo-field' label={t_('Parent FO')} field='parentFo'
        value={parentFoValue} fabricationOrderId={fabricationOrderId}
        absence={t_('Not reported')}
        editable={canEdit && (!source.parentFabricationOrder || Object.keys(source.parentFabricationOrder).length === 0 || !!source.parentFabricationOrder.id)}
        modified={isModified('parentFo')}
        onChange={handleFieldChange}
      />
      {/* H179 (CA9, CU8): el workflow de la OF. Vive en esta tarjeta —y no en una séptima— porque es lo
          que decide si la orden se planifica («Assigning the workflow makes this fabrication order
          plannable»), y porque los seis grupos y su orden son contrato de la HU 123 (S67). ⚠️ NO es un
          campo de la tanda: tiene su propia puerta (C2) y su propia confirmación (G12). */}
      <FabricationOrderWorkflowField
        fabricationOrderId={fabricationOrderId}
        onAssigned={onWorkflowAssigned}
        // G12: con cambios sin guardar en General, primero se resuelven ellos. Cambiar el workflow
        // recarga la ficha entera, y se llevaría por delante lo que el usuario tiene a medias.
        pendingGeneralChanges={touchedCount > 0}
        showAlert={showAlert}
      />
    </BlockCard>
  )

  // Las tres tarjetas que pueden quedarse vacías se arman aquí y se pintan EN UN SITIO O EN EL
  // OTRO —en su hueco, o dentro de la línea plegada del pie— sin escribirse dos veces: dos copias
  // del mismo JSX divergen en cuanto alguien toque una.
  // ⚠️⚠️ SE LLAMA DISTINTO QUE SU MITAD EDITABLE, Y ES UN DEFECTO QUE SE CREÓ AL QUITAR LOS
  // RÓTULOS DE SECCIÓN. Los dos grupos partidos repetían su título a propósito —ver «Origen
  // comercial» en las dos secciones evita que alguien busque el pedido debajo del cliente—, pero
  // lo que las desambiguaba eran las cabeceras «Lo que se puede cambiar» e «Identidad de la
  // orden». Sin ellas quedaron dos tarjetas con el mismo nombre y nada que las distinga.
  // La mitad de identidad se renombra por lo que contiene: planificación CALCULADA, la que saca el
  // planificador y nadie escribe a mano.
  const planningSystemCard = (
    <BlockCard testId='group-planning-and-delivery-system' title={t_('Calculated planning')}>
      <Box data-testid='planned-end-date-field' data-editable='false'>
        <ReadOnlyField label={t_('Planned end date')}>{formatDateTime(source.plannedEndDate)}</ReadOnlyField>
        {plannedEndDateLate && (
          <Typography data-testid='planned-end-date-warning' variant='caption' sx={{ display: 'block', color: 'warning.main', fontWeight: 700, mt: 0.5, ml: 1.75 }}>
            {t_('Planned end date exceeds target date')}
          </Typography>
        )}
      </Box>
    </BlockCard>
  )

  // ⚠️ La mitad EDITABLE de «Origen comercial»: solo el cliente. Conserva el título del grupo
  // aunque lleve un único campo — ver «Origen comercial» en las dos secciones es lo que impide que
  // alguien busque el pedido debajo del cliente y no lo encuentre.
  const commercialEditableCard = (
    <BlockCard testId='group-commercial-origin-editable' title={t_('Commercial origin')}>
      {(hasClient || clientEditable) && (
        <ReferenceFieldRow
          testId='client-field' label={t_('Client')} field='client'
          value={clientValue}
          displayValue={source.client?.name || source.client?.code || ''}
          options={clients} absence={t_('Not reported')}
          editable={clientEditable} modified={isModified('client')}
          onChange={handleFieldChange}
        />
      )}
    </BlockCard>
  )

  const commercialSystemCard = (
    <BlockCard testId='group-commercial-origin-system' title={t_('Sales order')}>
      {/* ADDENDUM 19, parte A (S342) — RN8: la ausencia se DECLARA, no se deja en blanco.
          Esta tarjeta enseñaba tres renglones vacíos bajo PEDIDO, ORDEN DE LÍNEA y FECHA DEL
          PEDIDO. Tres blancos no dicen "esta orden no viene de un pedido": no dicen nada, y quien
          los mira no sabe si es que no hay dato o es que la ficha no lo ha traído.

          Es UNA ausencia y no tres, y eso está medido, no supuesto: de las 110 órdenes, 104 (el
          94,5%) no tienen pedido, y Sun sirve ese caso como `order: null` + `lineOrder: null` —una
          ausencia entera, comprobada contra N1 en vivo—, no como un objeto con campos a null. Así
          que se dice una vez. Repetirla tres veces sería el mismo defecto que el PO señaló en la
          pasada 27 con los siete "Pending" en columna: el dato estaba bien y no informaba.

          La tarjeta NO desaparece cuando está vacía, al contrario que la de "Forecast" en la
          pasada 26. Aquella estaba vacía para TODAS las órdenes; esta tiene contenido real para 6.
          Una tarjeta que aparece y desaparece según la orden deja a quien mira sin saber si su
          ausencia significa "no hay pedido" o "algo ha fallado" — que es justo lo que RN8 evita. */}
      {/* ADDENDUM 21 A (S356): el aviso dice que no hay PEDIDO, no que no haya origen comercial.
          Con cliente presente —27 de 110— "sin origen comercial" sería sencillamente falso, y sale
          solo cuando no hay NI cliente NI pedido: con cliente, la tarjeta tiene algo que enseñar y
          un aviso de ausencia al lado de un dato presente confunde en vez de informar. */}
      {!hasOrder && !hasClient && (
        <Typography data-testid='no-commercial-origin' variant='body2' sx={{ color: 'text.secondary' }}>
          {t_('No linked sales order')}
        </Typography>
      )}
      {/* Los tres campos solo salen cuando HAY pedido. Y aun así llevan su propia declaración de
          ausencia: el esquema permite un pedido con `code`, `date` o `line_number` a null aunque
          hoy no haya ni un caso (0 de 6). Darlo por imposible porque este volcado no lo tenga
          sería decidir sobre el modelo desde un volcado — el error que esta historia ya cometió
          una vez. ⚠️ Si ese caso aparece algún día, la palabra habrá que mirarla con datos
          delante: se eligió la palabra que esta misma ficha ya usa para un dato que falta
          —"Sin informar" desde el addendum 30 A, antes "Pendiente"—, no por haber visto uno. */}
      {hasOrder && (
        <FieldRow testId='order-field' label={t_('Order')} value={source.order?.code} editable={false} absence={t_('Not reported')} />
      )}
      {hasOrder && (
        <FieldRow testId='line-order-field' label={t_('Line order')} value={source.lineOrder?.lineNumber} editable={false} absence={t_('Not reported')} />
      )}
      {/* ADDENDUM 10 (E16), punto 4: el fin planificado conserva la hora —es salida del
          planificador, y terminar a las 07:00 o a las 22:00 no es la misma situación—, pero la
          fecha del PEDIDO se muestra como día: es un dato del cliente y su hora es un artefacto
          de cuándo se registró la fila, no información que nadie vaya a leer. */}
      {hasOrder && (
        <FieldRow testId='order-date-field' label={t_('Order date')} value={formatDate(source.order?.date)} editable={false} absence={t_('Not reported')} />
      )}
    </BlockCard>
  )
  const forecastCard = (
    <BlockCard testId='group-forecast' title={t_('Forecast')}>
      {sheetReady
        ? (
          <BooleanFieldRow
            testId='forecast-field' label={t_('Forecast')}
            value={extendedValueOf('forecast')} editable={canEdit} modified={isModified('forecast')}
            onChange={(checked) => handleSheetFieldChange('forecast', checked)}
          />
          )
        : <SheetPendingFieldRow testId='forecast-field' label={t_('Forecast')} failed={sheetError} />}
      {/* ⚠️ CLAVE PROPIA, y no por costumbre: las dos candidatas que ya existen fallan por motivos
          distintos y los dos comprobados idioma a idioma.
          · `Forecast Expir. Date` es la del LISTADO para este mismo campo, pero está ROTA en 5 de
            los 8: la abreviatura "Expir." con punto se tradujo como fin de frase — catalán dice
            "La previsió caduca. Datar", alemán "Vorhersage läuft ab. Datum", portugués "Previsão
            de expiração. Data". Reutilizarla traería ese texto aquí. (El listado sigue con ella;
            es defecto suyo, anterior a esto, y va reportado — no arreglado de paso.)
          · `Expiration date` está limpia, pero es la caducidad de una acción de CALIDAD. Atarla
            haría que cambiar aquel rótulo cambiara éste, que es el enredo de siempre.
          El valor castellano es el que escribió el PO en el addendum: "Fecha de expiración". */}
      {sheetReady
        ? (
          <DateTimeFieldRow
            testId='expiration-date-field' label={t_('Forecast expiration date')} field='expirationDate'
            value={extendedValueOf('expirationDate')} editable={canEdit}
            modified={isModified('expirationDate')}
            onChange={handleSheetFieldChange} error={fieldErrors.expirationDate}
            dateTimeFormat={format.dateTime} formatDateTime={formatDateTime}
          />
          )
        : <SheetPendingFieldRow testId='expiration-date-field' label={t_('Forecast expiration date')} failed={sheetError} />}
    </BlockCard>
  )
  const additionalFieldsCard = (
    <BlockCard testId='group-additional-fields' title={t_('Additional fields')}>
      {extendedColumns.length === 0 && (
        <Typography data-testid='no-additional-fields' variant='body2' sx={{ color: 'text.secondary' }}>{t_('No additional fields defined')}</Typography>
      )}
      {extendedColumns.map((column) => (
        <Box
          key={column.field}
          data-testid={`extended-field-${column.field}`}
          data-editable={canEdit ? 'true' : 'false'}
          data-modified={isModified(column.field) ? 'true' : 'false'}
        >
          {!sheetReady && <ReadOnlyField label={column.name}><SheetPendingValue testId={`extended-field-${column.field}`} failed={sheetError} /></ReadOnlyField>}
          {/* S370: el control SUSTITUYE al texto, no se añade debajo.
              ⚠️ Esto pintaba el valor DOS VECES con permiso de edición: el `<Typography>` salía
              siempre y el input se añadía aparte, con el mismo dato dentro. Todos los campos
              vecinos —`EditableFieldRow`, `SelectFieldRow`— usan un ternario y sustituyen; este
              era el único que sumaba, y por eso se leía distinto de sus compañeros de tarjeta.
              Se arregla igualándolo a ellos, sin decidir nada nuevo sobre la convención. */}
          {sheetReady && !canEdit && (
            <ReadOnlyField label={column.name}>{formatExtendedValue(column, extendedValueOf(column.field), t_, formatDateTime)}</ReadOnlyField>
          )}
          {sheetReady && canEdit && column.type === 'boolean' && (
          // ⚠️ Esta casilla y la de `BooleanFieldRow` (S379, la tarjeta de Previsión) son la
          // misma receta escrita dos veces. No se unificaron porque aquí el rótulo y el
          // envoltorio se pintan FUERA del `if`, compartidos con las ramas de texto: usar el
          // componente exigiría reestructurar este bloque y volver a tocar el arreglo de S370.
          // Si algún día se toca una, hay que tocar la otra.
            <FormControlLabel
              label={column.name}
              sx={{ minHeight: 40, ml: 0, ...(isModified(column.field) ? { color: 'warning.main' } : {}) }}
              control={(
                <Checkbox
                  checked={!!extendedValueOf(column.field)}
                  onChange={(event) => handleSheetFieldChange(column.field, event.target.checked)}
                  size='small'
                  inputProps={{ 'data-testid': `extended-field-${column.field}-input`, 'aria-label': column.name }}
                />
              )}
            />
          )}
          {/* S450: a date extended column uses the same picker as the fixed dates (day and time, sent
              untruncated by `buildSaveBody`). */}
          {sheetReady && canEdit && column.type === 'dateTimePicker' && (
            <DetailDateTimePicker
              testId={`extended-field-${column.field}`}
              label={column.name}
              value={extendedValueOf(column.field)}
              dateTimeFormat={format.dateTime}
              modified={isModified(column.field)}
              error={fieldErrors[column.field]}
              onChange={(newValue) => handleSheetFieldChange(column.field, newValue)}
            />
          )}
          {sheetReady && canEdit && column.type === 'smartSelect' && (
            <ExtendedListInput
              column={column}
              value={extendedValueOf(column.field) === null || extendedValueOf(column.field) === undefined ? '' : extendedValueOf(column.field)}
              modified={isModified(column.field)}
              onChange={handleSheetFieldChange}
            />
          )}
          {sheetReady && canEdit && column.type !== 'boolean' && column.type !== 'dateTimePicker' && column.type !== 'smartSelect' && (
            <TextField
              size='small'
              fullWidth
              label={column.name}
              error={!!fieldErrors[column.field]}
              value={extendedValueOf(column.field) === null || extendedValueOf(column.field) === undefined ? '' : extendedValueOf(column.field)}
              onChange={(event) => handleSheetFieldChange(column.field, event.target.value)}
              // Text and not `type='number'`: with a comma decimal the browser answers `''` and the
              // saved value would be erased. The comma is normalised when the batch is validated and sent.
              inputProps={{ 'data-testid': `extended-field-${column.field}-input`, 'aria-label': column.name, inputMode: column.numeric ? 'decimal' : 'text' }}
              sx={fieldSx(theme, isModified(column.field), fieldErrors[column.field])}
            />
          )}
          {fieldErrors[column.field] && (
            <Typography data-testid={`extended-field-${column.field}-error`} variant='caption' color='error' sx={{ display: 'block', mt: 0.5, ml: 1.75 }}>{fieldErrors[column.field]}</Typography>
          )}
        </Box>
      ))}
    </BlockCard>
  )

  return (
    <Box data-testid='general-block'>
      {/* Task 29 fix r1: X2 failed — one Retry for every field it feeds, above their cards. */}
      {!sheetReady && sheetError && (
        <Stack data-testid='sheet-error' direction='row' spacing={1.5} alignItems='center' sx={{ mb: 2 }}>
          <Typography variant='body2' sx={{ color: 'error.main', fontWeight: 700 }}>{t_('Could not load this block')}</Typography>
          <Button data-testid='sheet-retry' size='small' variant='outlined' onClick={onRetrySheet} sx={{ textTransform: 'none', borderRadius: CONTROL_RADIUS }}>{t_('Retry')}</Button>
        </Stack>
      )}
      {/* ⚠️⚠️ DOS SECCIONES, SEIS SUBGRUPOS. El lienzo reparte la ficha en «lo que se puede
          cambiar» y «identidad de la orden», y el PO pone una condición encima: «respetando en lo
          que se pueda la estructura de datos que tenía el mockup inicial». Por eso los seis
          títulos SOBREVIVEN dentro de las secciones en vez de disolverse en dos listas planas:
          «prioridad» y «cliente» seguidos, sin más, no dicen que uno es planificación y el otro
          procedencia comercial.
          Cuatro grupos se mudan enteros; DOS se parten —planificación y entrega, y origen
          comercial— porque tienen campos a los dos lados, y las dos mitades conservan el título. */}
      <Box data-testid='section-editable'>
        {planningEditableCard}
        {commercialEditableCard}
        {!forecastFoldable && forecastCard}
        {!additionalFieldsFoldable && additionalFieldsCard}
      </Box>

      <Box data-testid='section-identity'>
        {identificationCard}
        {quantitiesCard}
        {planningSystemCard}
        {!commercialOriginFoldable && commercialSystemCard}
      </Box>

      {/* §2.3 — la línea del pie recoge lo que no tiene nada que enseñar. ENUMERA cada cosa
          plegada CON SU VALOR, así que todo se sigue leyendo sin abrir nada, y «Ver todos los
          campos» devuelve las tarjetas ENTERAS: las mismas, no una copia. */}
      {foldedSummary.length > 0 && (
        <CollapsedFieldsLine
          testId='general-folded-fields'
          summary={foldedSummary}
          openLabel={t_('See all fields')}
        >
          {commercialOriginFoldable && commercialSystemCard}
          {forecastFoldable && forecastCard}
          {additionalFieldsFoldable && additionalFieldsCard}
        </CollapsedFieldsLine>
      )}

      <GeneralSaveBar
        touchedCount={touchedCount}
        touchedLabels={touchedLabels}
        sheetReady={sheetReady}
        sheetError={sheetError}
        onRetrySheet={onRetrySheet}
        saveError={saveError}
        saving={saving}
        onDiscard={handleDiscard}
        onSave={handleSave}
        conflict={conflict}
        conflictFieldLabel={conflictFieldLabel}
        deliveryConfirmation={deliveryConfirmation}
        onConfirmDelivery={handleConfirmDelivery}
        onCancelDeliveryConfirmation={handleCancelDeliveryConfirmation}
      />
    </Box>
  )
}

export default GeneralBlock
